import { useEffect, useMemo, useState } from 'react'
import VideoCard from "./VideoCard"
import { learningApi } from '@/utils/api'
import { useAuth } from '@/context/AuthContext'
import { resolveBackendUrl } from '@/utils/media'

interface ModulesListProps {
  activeTab: 'all' | 'recommended'
}

interface Video {
  id: string
  title?: string
  duration?: number
  orderIndex?: number
  videoUrl?: string
  thumbnail?: string
  watchedPercentage?: number
  isCompleted?: boolean
}

interface Module {
  id: string
  title: string
  description?: string
  category?: string
  thumbnail?: string
  isRecommended?: boolean
  videos: Video[]
  progressPercentage?: number
  isCompleted?: boolean
}

export default function ModulesList({ activeTab }: ModulesListProps) {
  const { user } = useAuth()
  const coupleId = (user as { coupleId?: string })?.coupleId
  const [modules, setModules] = useState<Module[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const sortedModules = useMemo(
    () =>
      [...modules].sort((a, b) => (a.title || '').localeCompare(b.title || '')),
    [modules]
  )

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const [allRes, recRes, progressRes] = await Promise.all([
          learningApi.listModules(),
          coupleId ? learningApi.recommendedModules(coupleId) : Promise.resolve(null),
          coupleId ? learningApi.progress(coupleId) : Promise.resolve(null),
        ])

        const allModules: Module[] =
          (allRes as { data?: Module[] }).data || (allRes as unknown as Module[]) || []
        const normalizedModules = allModules.map((module) => ({
          ...module,
          thumbnail: module.thumbnail ? resolveBackendUrl(module.thumbnail) : undefined,
          videos: (module.videos || []).map((video) => {
            const { videoUrl, thumbnail: videoThumb, ...rest } = video
            return {
              ...rest,
              ...(videoUrl && { videoUrl: resolveBackendUrl(videoUrl) }),
              ...(videoThumb && { thumbnail: resolveBackendUrl(videoThumb) }),
            }
          }),
        }))
        const recommendedIds =
          (recRes as { data?: Module[] })?.data?.map((m) => m.id) ||
          ((recRes as unknown as Module[]) || []).map((m) => (m as Module).id)
        const progress =
          (progressRes as { data?: Array<{ moduleId: string; progressPercentage?: number; isCompleted?: boolean }> })
            ?.data || []

        const merged = normalizedModules.map((m) => {
          const p = progress.find((pr) => pr.moduleId === m.id)
          const { thumbnail, ...rest } = m
          return {
            ...rest,
            ...(thumbnail && { thumbnail }),
            isRecommended: recommendedIds?.includes(m.id) ?? m.isRecommended ?? false,
            progressPercentage: p?.progressPercentage ?? m.progressPercentage ?? 0,
            isCompleted: p?.isCompleted ?? m.isCompleted ?? false,
          }
        })

        // Fetch per-video progress
        if (coupleId) {
          const allVideos = merged.flatMap(m => m.videos.map(v => ({ ...v, moduleId: m.id })))
          const videoProgressResults = await Promise.allSettled(
            allVideos.map(v =>
              learningApi.getVideoProgress(v.id, coupleId!)
                .then(res => {
                  const data = (res as { data?: { watchedSeconds?: number; totalDuration?: number; isCompleted?: boolean } }).data
                    || (res as unknown as { watchedSeconds?: number; totalDuration?: number; isCompleted?: boolean })
                  const pct = data?.totalDuration && data.totalDuration > 0
                    ? Math.round((data.watchedSeconds || 0) / data.totalDuration * 100)
                    : 0
                  return { videoId: v.id, watchedPercentage: pct, isCompleted: data?.isCompleted ?? false }
                })
            )
          )
          const videoProgressMap = new Map<string, { watchedPercentage: number; isCompleted: boolean }>()
          videoProgressResults.forEach((result, idx) => {
            if (result.status === 'fulfilled' && allVideos[idx]) {
              videoProgressMap.set(allVideos[idx].id, result.value)
            }
          })

          const enriched = merged.map(m => ({
            ...m,
            videos: m.videos.map(v => {
              const vp = videoProgressMap.get(v.id)
              return {
                ...v,
                watchedPercentage: vp?.watchedPercentage ?? 0,
                isCompleted: vp?.isCompleted ?? false,
              }
            }),
          }))

          setModules(
            activeTab === 'recommended'
              ? enriched.filter((m) => m.isRecommended)
              : enriched
          )
        } else {
          setModules(
            activeTab === 'recommended'
              ? merged.filter((m) => m.isRecommended)
              : merged
          )
        }
      } catch (err) {
        setError((err as Error)?.message || 'Unable to load modules.')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [activeTab, coupleId])

  if (loading) {
    return <p className='text-[14px] text-[#4e4d4d]'>Loading modules...</p>
  }

  if (error) {
    return <p className='text-[14px] text-red-600'>{error}</p>
  }

  if (sortedModules.length === 0) {
    return <p className='text-[14px] text-[#4e4d4d]'>No modules available.</p>
  }

  return (
    <div className='flex flex-col gap-8'>
      {sortedModules.map((module) => (
        <div key={module.id}>
          {/* Module Header */}
          <div className='flex items-center justify-between mb-4'>
            <div className='flex items-center gap-2'>
              <svg className='h-5 w-5 text-[#151515]' fill='currentColor' viewBox='0 0 20 20'>
                <path d='M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z' />
              </svg>
              <h3 className='text-[18px] font-bold text-[#151515]'>{module.title}</h3>
            </div>
            <div className='flex items-center gap-3'>
              {module.isCompleted ? (
                <span className='flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-50 border border-green-200 text-sm font-semibold text-green-700'>
                  <svg width='14' height='14' viewBox='0 0 24 24' fill='none'>
                    <circle cx='12' cy='12' r='12' fill='#0ECF5B' />
                    <path d='M8 12l3 3 5-6' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                  Completed
                </span>
              ) : (module.progressPercentage ?? 0) > 0 ? (
                <div className='flex items-center gap-2'>
                  <div className='w-24 h-2 rounded-full bg-gray-200 overflow-hidden'>
                    <div
                      className='h-full rounded-full bg-[#FA6017] transition-all duration-300'
                      style={{ width: `${module.progressPercentage ?? 0}%` }}
                    />
                  </div>
                  <span className='text-[12px] font-semibold text-[#4e4d4d]'>
                    {Math.round(module.progressPercentage ?? 0)}%
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          {/* Video Grid - 5 columns */}
          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4'>
            {module.videos?.map((video, idx) => (
              <VideoCard
                key={video.id || `${module.id}-v-${idx}`}
                video={{
                  id: video.id,
                  title: video.title || `Video ${idx + 1}`,
                  duration: video.duration ?? 0,
                  orderIndex: video.orderIndex ?? idx + 1,
                  videoUrl: video.videoUrl ?? '',
                }}
                moduleId={module.id}
                moduleTitle={module.title}
                moduleThumbnail={video.thumbnail || module.thumbnail}
                watchedPercentage={video.watchedPercentage}
                isCompleted={video.isCompleted}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
